import { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthProvider";
import { Admin } from "../lib/api";
import { useAdminAlertsRealtime } from "../hooks/useAdminAlertsRealtime";

// ════════════════════════════════════════════════════════════
// Types
// ════════════════════════════════════════════════════════════

type RuleType =
  | "new_user_register"
  | "user_reported"
  | "suspicious_ip"
  | "post_boosted"
  | "server_high_cpu"
  | "outbox_failed"
  | "login_failures";

type ActionType = "email" | "sse" | "push";

interface NotificationRule {
  _id: string;
  type: RuleType;
  threshold: number;
  timeRange: number; // minutes
  action: ActionType;
  targets: string[]; // array of admin emails or "all-admins"
  status: "active" | "inactive";
  createdAt: string;
  updatedAt: string;
}

type AlertSeverity = "HIGH" | "MEDIUM" | "LOW";
type AlertStatus = "open" | "acknowledged" | "resolved";

type AlertType =
  | "server_high_cpu"
  | "server_high_memory"
  | "mongodb_slow_query"
  | "outbox_fail_count"
  | "suspicious_ip_detected"
  | "login_failure_spike";

interface SystemAlert {
  _id: string;
  type: AlertType;
  severity: AlertSeverity;
  message: string;
  source?: string | null;
  stackTrace?: string | null;
  meta?: Record<string, unknown> | null;
  status: AlertStatus;
  acknowledgedAt?: string | null;
  acknowledgedBy?: string | null;
  resolvedAt?: string | null;
  resolvedBy?: string | null;
  timestamp: string;
}

type TabType = "rules" | "alerts";
type FormMode = "create" | "edit";

// ════════════════════════════════════════════════════════════
// Main Component
// ════════════════════════════════════════════════════════════

export default function AdminNotificationRules() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  // FAZ 16: SSE realtime connection indicator
  const { connected: sseConnected, refetch: refetchAlerts } = useAdminAlertsRealtime();

  // States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // FAZ 16: Read tab from query param (?tab=alerts or ?tab=rules)
  const tabFromQuery = searchParams.get("tab") as TabType | null;
  const [activeTab, setActiveTab] = useState<TabType>(
    tabFromQuery === "alerts" || tabFromQuery === "rules" ? tabFromQuery : "rules"
  );

  // Rules data
  const [rules, setRules] = useState<NotificationRule[]>([]);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);

  // Search & filters
  const [searchQuery, setSearchQuery] = useState("");

  // Form state (for rules)
  const [formMode, setFormMode] = useState<FormMode>("create");
  const [formData, setFormData] = useState<Partial<NotificationRule>>({
    type: "new_user_register",
    threshold: 10,
    timeRange: 60,
    action: "email",
    targets: [],
    status: "active",
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formBusy, setFormBusy] = useState(false);

  // Detail panel (for alerts)
  const [selectedAlert, setSelectedAlert] = useState<SystemAlert | null>(null);
  const [actionBusy, setActionBusy] = useState(false);

  // Check admin permission
  if (!user?.isAdmin) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <h1>🚫 Erişim Engellendi</h1>
        <p>Bu sayfa için admin yetkisi gerekli.</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Load Functions
  // ─────────────────────────────────────────────────────────

  async function loadRules() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.notificationListRules();
      if (response?.ok && response?.rules) {
        setRules(response.rules);
      } else if (Array.isArray(response)) {
        setRules(response);
      } else {
        // Mock data for development
        setRules([
          {
            _id: "1",
            type: "login_failures",
            threshold: 10,
            timeRange: 60,
            action: "email",
            targets: ["all-admins"],
            status: "active",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          },
          {
            _id: "2",
            type: "suspicious_ip",
            threshold: 5,
            timeRange: 30,
            action: "sse",
            targets: ["admin@savnot.com"],
            status: "active",
            createdAt: new Date(Date.now() - 86400000).toISOString(),
            updatedAt: new Date(Date.now() - 86400000).toISOString(),
          },
          {
            _id: "3",
            type: "outbox_failed",
            threshold: 20,
            timeRange: 120,
            action: "push",
            targets: ["all-admins"],
            status: "inactive",
            createdAt: new Date(Date.now() - 172800000).toISOString(),
            updatedAt: new Date(Date.now() - 172800000).toISOString(),
          },
        ]);
      }
    } catch (e: any) {
      console.error("Rules load failed:", e);
      setError(e?.message || "Notification rules yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadAlerts() {
    try {
      setLoading(true);
      setError(null);
      const response = await Admin.notificationListSystemAlerts({ limit: 100 });
      if (response?.ok && response?.alerts) {
        setAlerts(response.alerts);
      } else if (Array.isArray(response)) {
        setAlerts(response);
      } else {
        // Mock data for development
        setAlerts([
          {
            _id: "1",
            type: "server_high_cpu",
            severity: "HIGH",
            message: "CPU kullanımı %95'in üzerinde (5 dakikadır)",
            source: "SavNot Backend v1.9",
            status: "open",
            timestamp: new Date(Date.now() - 300000).toISOString(),
          },
          {
            _id: "2",
            type: "login_failure_spike",
            severity: "MEDIUM",
            message: "Son 10 dakikada 47 başarısız login denemesi tespit edildi",
            source: "Auth Service",
            status: "acknowledged",
            acknowledgedAt: new Date(Date.now() - 60000).toISOString(),
            acknowledgedBy: "admin@savnot.com",
            timestamp: new Date(Date.now() - 600000).toISOString(),
          },
          {
            _id: "3",
            type: "outbox_fail_count",
            severity: "LOW",
            message: "Outbox'ta 12 failed event birikti",
            source: "Outbox Worker",
            status: "resolved",
            resolvedAt: new Date(Date.now() - 3600000).toISOString(),
            resolvedBy: "admin@savnot.com",
            timestamp: new Date(Date.now() - 7200000).toISOString(),
          },
        ]);
      }
    } catch (e: any) {
      console.error("Alerts load failed:", e);
      setError(e?.message || "System alerts yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }

  async function loadData() {
    if (activeTab === "rules") {
      await loadRules();
    } else {
      await loadAlerts();
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ─────────────────────────────────────────────────────────
  // CRUD Operations (Rules)
  // ─────────────────────────────────────────────────────────

  function validateForm(): boolean {
    const errors: Record<string, string> = {};

    if (!formData.type) {
      errors.type = "Rule tipi zorunlu";
    }
    if (!formData.threshold || formData.threshold <= 0) {
      errors.threshold = "Threshold > 0 olmalı";
    }
    if (!formData.targets || formData.targets.length === 0) {
      errors.targets = "Targets boş olamaz";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleCreateRule() {
    if (!validateForm()) return;

    setFormBusy(true);
    try {
      const response = await Admin.notificationCreateRule(formData);
      if (response?.ok) {
        alert("✅ Rule başarıyla oluşturuldu!");
        await loadRules();
        resetForm();
      }
    } catch (e: any) {
      console.error("Create rule failed:", e);
      alert(`❌ Hata: ${e?.message || "Rule oluşturulamadı."}`);
    } finally {
      setFormBusy(false);
    }
  }

  async function handleUpdateRule() {
    if (!validateForm() || !formData._id) return;

    setFormBusy(true);
    try {
      const response = await Admin.notificationUpdateRule(formData._id, formData);
      if (response?.ok) {
        alert("✅ Rule başarıyla güncellendi!");
        await loadRules();
        resetForm();
      }
    } catch (e: any) {
      console.error("Update rule failed:", e);
      alert(`❌ Hata: ${e?.message || "Rule güncellenemedi."}`);
    } finally {
      setFormBusy(false);
    }
  }

  async function handleDeleteRule(id: string) {
    if (!confirm("Bu rule'u silmek istediğinizden emin misiniz?")) {
      return;
    }

    try {
      const response = await Admin.notificationDeleteRule(id);
      if (response?.ok) {
        alert("✅ Rule başarıyla silindi!");
        await loadRules();
        if (formData._id === id) {
          resetForm();
        }
      }
    } catch (e: any) {
      console.error("Delete rule failed:", e);
      alert(`❌ Hata: ${e?.message || "Rule silinemedi."}`);
    }
  }

  function handleEditRule(rule: NotificationRule) {
    setFormMode("edit");
    setFormData({ ...rule });
    setFormErrors({});
  }

  function resetForm() {
    setFormMode("create");
    setFormData({
      type: "new_user_register",
      threshold: 10,
      timeRange: 60,
      action: "email",
      targets: [],
      status: "active",
    });
    setFormErrors({});
  }

  // ─────────────────────────────────────────────────────────
  // Alert Actions
  // ─────────────────────────────────────────────────────────

  async function handleAcknowledgeAlert(id: string) {
    setActionBusy(true);
    try {
      const response = await Admin.notificationAcknowledgeAlert(id);
      if (response?.ok) {
        alert("✅ Alert acknowledged!");
        await loadAlerts();
        if (selectedAlert?._id === id) {
          const updated = alerts.find((a) => a._id === id);
          if (updated) setSelectedAlert(updated);
        }
      }
    } catch (e: any) {
      console.error("Acknowledge alert failed:", e);
      alert(`❌ Hata: ${e?.message || "Alert acknowledge edilemedi."}`);
    } finally {
      setActionBusy(false);
    }
  }

  async function handleResolveAlert(id: string) {
    setActionBusy(true);
    try {
      const response = await Admin.notificationResolveAlert(id);
      if (response?.ok) {
        alert("✅ Alert resolved!");
        await loadAlerts();
        setSelectedAlert(null);
      }
    } catch (e: any) {
      console.error("Resolve alert failed:", e);
      alert(`❌ Hata: ${e?.message || "Alert resolve edilemedi."}`);
    } finally {
      setActionBusy(false);
    }
  }

  // ─────────────────────────────────────────────────────────
  // Filter Data
  // ─────────────────────────────────────────────────────────

  const filteredRules = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return rules;

    return rules.filter(
      (rule) =>
        rule.type.toLowerCase().includes(query) ||
        rule.action.toLowerCase().includes(query) ||
        rule.targets.some((t) => t.toLowerCase().includes(query))
    );
  }, [rules, searchQuery]);

  const filteredAlerts = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    if (!query) return alerts;

    return alerts.filter(
      (alert) =>
        alert.type.toLowerCase().includes(query) ||
        alert.message.toLowerCase().includes(query) ||
        alert.severity.toLowerCase().includes(query) ||
        alert.source?.toLowerCase().includes(query)
    );
  }, [alerts, searchQuery]);

  // ─────────────────────────────────────────────────────────
  // Helper Functions
  // ─────────────────────────────────────────────────────────

  function formatDate(dateStr?: string | null) {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("tr-TR");
    } catch {
      return dateStr;
    }
  }

  function timeAgo(dateStr?: string | null) {
    if (!dateStr) return "";
    try {
      const now = Date.now();
      const then = new Date(dateStr).getTime();
      const diffMs = now - then;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMins / 60);
      const diffDays = Math.floor(diffHours / 24);

      if (diffMins < 1) return "Az önce";
      if (diffMins < 60) return `${diffMins} dk önce`;
      if (diffHours < 24) return `${diffHours} saat önce`;
      return `${diffDays} gün önce`;
    } catch {
      return "";
    }
  }

  function getStatusBadge(status: string) {
    if (status === "active") {
      return (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            backgroundColor: "rgba(34, 197, 94, 0.15)",
            color: "#22c55e",
            border: "1px solid rgba(34, 197, 94, 0.4)",
          }}
        >
          ✅ Aktif
        </span>
      );
    }
    return (
      <span
        style={{
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          backgroundColor: "rgba(156, 163, 175, 0.15)",
          color: "#9ca3af",
          border: "1px solid rgba(156, 163, 175, 0.4)",
        }}
      >
        ⏸ Pasif
      </span>
    );
  }

  function getSeverityBadge(severity: AlertSeverity) {
    const styles: Record<AlertSeverity, { bg: string; color: string; border: string; label: string }> = {
      HIGH: { bg: "rgba(239, 68, 68, 0.15)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.4)", label: "🔴 HIGH" },
      MEDIUM: { bg: "rgba(249, 115, 22, 0.15)", color: "#f97316", border: "1px solid rgba(249, 115, 22, 0.4)", label: "🟠 MEDIUM" },
      LOW: { bg: "rgba(59, 130, 246, 0.15)", color: "#3b82f6", border: "1px solid rgba(59, 130, 246, 0.4)", label: "🔵 LOW" },
    };
    const style = styles[severity];
    return (
      <span
        style={{
          ...style,
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {style.label}
      </span>
    );
  }

  function getAlertStatusBadge(status: AlertStatus) {
    const styles: Record<AlertStatus, { bg: string; color: string; border: string; label: string }> = {
      open: { bg: "rgba(239, 68, 68, 0.15)", color: "#ef4444", border: "1px solid rgba(239, 68, 68, 0.4)", label: "🔴 Open" },
      acknowledged: { bg: "rgba(251, 191, 36, 0.15)", color: "#fbbf24", border: "1px solid rgba(251, 191, 36, 0.4)", label: "👁 Acknowledged" },
      resolved: { bg: "rgba(34, 197, 94, 0.15)", color: "#22c55e", border: "1px solid rgba(34, 197, 94, 0.4)", label: "✔ Resolved" },
    };
    const style = styles[status];
    return (
      <span
        style={{
          ...style,
          padding: "4px 10px",
          borderRadius: 6,
          fontSize: 12,
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        {style.label}
      </span>
    );
  }

  // Loading state
  if (loading && ((activeTab === "rules" && filteredRules.length === 0) || (activeTab === "alerts" && filteredAlerts.length === 0))) {
    return (
      <div style={{ padding: 24, textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <p style={{ color: "var(--muted)" }}>Notification verileri yükleniyor...</p>
      </div>
    );
  }

  // ─────────────────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────────────────

  return (
    <div style={{ padding: 24, maxWidth: 1600, margin: "0 auto" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 24,
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <h1 style={{ fontSize: 28, fontWeight: 800, margin: 0, color: "var(--text)" }}>🔔 Notification Rules & System Alerts</h1>
        
        {/* FAZ 16: SSE Connection Indicator */}
        {sseConnected && (
          <div
            title="SSE bağlantısı aktif - Canlı güncellemeler alınıyor"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 12px",
              fontSize: 12,
              fontWeight: 700,
              borderRadius: 8,
              backgroundColor: "rgba(34, 197, 94, 0.1)",
              border: "1px solid rgba(34, 197, 94, 0.3)",
              color: "#22c55e",
              cursor: "default",
            }}
          >
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "#22c55e",
                boxShadow: "0 0 8px rgba(34, 197, 94, 0.6)",
                animation: "pulse-glow 2s cubic-bezier(0.4, 0, 0.6, 1) infinite",
              }}
            />
            <span>LIVE</span>
          </div>
        )}
        
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => loadData()}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(59, 130, 246, 0.4)",
              backgroundColor: "rgba(59, 130, 246, 0.1)",
              color: "#3b82f6",
              cursor: "pointer",
            }}
          >
            <span>🔄</span>
            <span>Yenile</span>
          </button>
          <button
            onClick={() => navigate("/settings")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "8px 16px",
              fontSize: 14,
              fontWeight: 600,
              borderRadius: 8,
              border: "1px solid rgba(156, 163, 175, 0.4)",
              backgroundColor: "rgba(156, 163, 175, 0.1)",
              color: "#9ca3af",
              cursor: "pointer",
            }}
          >
            <span>←</span>
            <span>Geri</span>
          </button>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div
          style={{
            padding: 16,
            marginBottom: 16,
            borderRadius: 10,
            backgroundColor: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.4)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 20 }}>⚠️</span>
            <div>
              <div style={{ fontWeight: 700, color: "#ef4444" }}>Veri yüklenemedi</div>
              <div style={{ fontSize: 14, color: "#ef4444", opacity: 0.9 }}>{error}</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              onClick={() => loadData()}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(239, 68, 68, 0.4)",
                backgroundColor: "rgba(239, 68, 68, 0.15)",
                color: "#ef4444",
                cursor: "pointer",
              }}
            >
              🔄 Yeniden Dene
            </button>
            <button
              onClick={() => setError(null)}
              style={{
                padding: "6px 12px",
                fontSize: 13,
                fontWeight: 600,
                borderRadius: 6,
                border: "1px solid rgba(156, 163, 175, 0.4)",
                backgroundColor: "rgba(156, 163, 175, 0.1)",
                color: "#9ca3af",
                cursor: "pointer",
              }}
            >
              ✕
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", padding: "4px 0" }}>
        {[
          { key: "rules", label: "⚙️ Notification Rules" },
          { key: "alerts", label: "🚨 System Alerts" },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => {
              const newTab = tab.key as TabType;
              setActiveTab(newTab);
              setSearchQuery("");
              
              // FAZ 16: Update query param
              navigate(`/admin/notification-rules?tab=${newTab}`, { replace: true });
              
              if (newTab === "alerts") {
                setSelectedAlert(null);
              } else {
                resetForm();
              }
            }}
            style={{
              flex: "1 1 auto",
              padding: "10px 16px",
              fontSize: 14,
              fontWeight: 700,
              borderRadius: 8,
              border: activeTab === tab.key ? "1px solid rgba(59, 130, 246, 0.5)" : "1px solid rgba(156, 163, 175, 0.3)",
              backgroundColor: activeTab === tab.key ? "rgba(59, 130, 246, 0.15)" : "var(--bg2)",
              color: activeTab === tab.key ? "#3b82f6" : "var(--text)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          type="text"
          placeholder={activeTab === "rules" ? "🔍 Ara (type, action, targets...)" : "🔍 Ara (type, message, severity, source...)"}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            width: "100%",
            padding: "10px 12px",
            fontSize: 14,
            borderRadius: 8,
            border: "1px solid rgba(156, 163, 175, 0.3)",
            backgroundColor: "var(--bg2)",
            color: "var(--text)",
          }}
        />
      </div>

      {/* Main Content Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 3fr) minmax(0, 1.2fr)", gap: 16 }}>
        {/* Left: List */}
        <div>
          {activeTab === "rules" ? (
            /* RULES TAB */
            <div
              style={{
                overflowX: "auto",
                borderRadius: 10,
                border: "1px solid rgba(156, 163, 175, 0.2)",
                backgroundColor: "var(--bg2)",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ backgroundColor: "rgba(156, 163, 175, 0.08)" }}>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Rule Tipi</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Threshold</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Time Range</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Action</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Status</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRules.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>
                        {searchQuery ? "Arama sonucu bulunamadı" : "Henüz rule yok"}
                      </td>
                    </tr>
                  )}
                  {filteredRules.map((rule) => (
                    <tr
                      key={rule._id}
                      style={{
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                        backgroundColor: formData._id === rule._id ? "rgba(59, 130, 246, 0.08)" : "transparent",
                      }}
                    >
                      <td style={{ padding: 12, fontWeight: 600 }}>{rule.type}</td>
                      <td style={{ padding: 12, color: "#3b82f6", fontWeight: 700 }}>≥ {rule.threshold}</td>
                      <td style={{ padding: 12 }}>{rule.timeRange} dk</td>
                      <td style={{ padding: 12 }}>
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: 6,
                            fontSize: 11,
                            fontWeight: 600,
                            backgroundColor: "rgba(168, 85, 247, 0.15)",
                            color: "#a855f7",
                            border: "1px solid rgba(168, 85, 247, 0.3)",
                          }}
                        >
                          {rule.action}
                        </span>
                      </td>
                      <td style={{ padding: 12 }}>{getStatusBadge(rule.status)}</td>
                      <td style={{ padding: 12 }}>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => handleEditRule(rule)}
                            style={{
                              padding: "4px 10px",
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 6,
                              border: "1px solid rgba(59, 130, 246, 0.4)",
                              backgroundColor: "rgba(59, 130, 246, 0.1)",
                              color: "#3b82f6",
                              cursor: "pointer",
                            }}
                          >
                            ✏ Düzenle
                          </button>
                          <button
                            onClick={() => handleDeleteRule(rule._id)}
                            style={{
                              padding: "4px 10px",
                              fontSize: 12,
                              fontWeight: 600,
                              borderRadius: 6,
                              border: "1px solid rgba(239, 68, 68, 0.4)",
                              backgroundColor: "rgba(239, 68, 68, 0.1)",
                              color: "#ef4444",
                              cursor: "pointer",
                            }}
                          >
                            🗑 Sil
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* ALERTS TAB */
            <div
              style={{
                overflowX: "auto",
                borderRadius: 10,
                border: "1px solid rgba(156, 163, 175, 0.2)",
                backgroundColor: "var(--bg2)",
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr style={{ backgroundColor: "rgba(156, 163, 175, 0.08)" }}>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Alert Tipi</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Severity</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Mesaj</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Status</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>Zaman</th>
                    <th style={{ padding: 12, textAlign: "left", fontWeight: 700, color: "var(--muted)" }}>İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAlerts.length === 0 && (
                    <tr>
                      <td colSpan={6} style={{ padding: 24, textAlign: "center", color: "var(--muted)" }}>
                        {searchQuery ? "Arama sonucu bulunamadı" : "Henüz alert yok"}
                      </td>
                    </tr>
                  )}
                  {filteredAlerts.map((alert) => (
                    <tr
                      key={alert._id}
                      onClick={() => setSelectedAlert(alert)}
                      style={{
                        cursor: "pointer",
                        borderBottom: "1px solid rgba(156, 163, 175, 0.1)",
                        backgroundColor: selectedAlert?._id === alert._id ? "rgba(59, 130, 246, 0.12)" : "transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (selectedAlert?._id !== alert._id) e.currentTarget.style.backgroundColor = "rgba(156, 163, 175, 0.05)";
                      }}
                      onMouseLeave={(e) => {
                        if (selectedAlert?._id !== alert._id) e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <td style={{ padding: 12, fontWeight: 600 }}>{alert.type}</td>
                      <td style={{ padding: 12 }}>{getSeverityBadge(alert.severity)}</td>
                      <td
                        style={{
                          padding: 12,
                          maxWidth: 300,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {alert.message}
                      </td>
                      <td style={{ padding: 12 }}>{getAlertStatusBadge(alert.status)}</td>
                      <td style={{ padding: 12, fontSize: 12, color: "var(--muted)" }}>
                        <div>{formatDate(alert.timestamp)}</div>
                        <div style={{ fontSize: 11, opacity: 0.7 }}>{timeAgo(alert.timestamp)}</div>
                      </td>
                      <td style={{ padding: 12 }}>
                        <div style={{ display: "flex", gap: 4 }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedAlert(alert);
                            }}
                            style={{
                              padding: "4px 8px",
                              fontSize: 11,
                              fontWeight: 600,
                              borderRadius: 6,
                              border: "1px solid rgba(59, 130, 246, 0.4)",
                              backgroundColor: "rgba(59, 130, 246, 0.1)",
                              color: "#3b82f6",
                              cursor: "pointer",
                            }}
                          >
                            👁
                          </button>
                          {alert.status === "open" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAcknowledgeAlert(alert._id);
                              }}
                              disabled={actionBusy}
                              style={{
                                padding: "4px 8px",
                                fontSize: 11,
                                fontWeight: 600,
                                borderRadius: 6,
                                border: "1px solid rgba(251, 191, 36, 0.4)",
                                backgroundColor: "rgba(251, 191, 36, 0.1)",
                                color: "#fbbf24",
                                cursor: actionBusy ? "not-allowed" : "pointer",
                                opacity: actionBusy ? 0.6 : 1,
                              }}
                            >
                              ⚠
                            </button>
                          )}
                          {alert.status !== "resolved" && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleResolveAlert(alert._id);
                              }}
                              disabled={actionBusy}
                              style={{
                                padding: "4px 8px",
                                fontSize: 11,
                                fontWeight: 600,
                                borderRadius: 6,
                                border: "1px solid rgba(34, 197, 94, 0.4)",
                                backgroundColor: "rgba(34, 197, 94, 0.1)",
                                color: "#22c55e",
                                cursor: actionBusy ? "not-allowed" : "pointer",
                                opacity: actionBusy ? 0.6 : 1,
                              }}
                            >
                              ✔
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Right: Form (Rules) or Detail Panel (Alerts) */}
        <div
          style={{
            borderRadius: 10,
            border: "1px solid rgba(156, 163, 175, 0.2)",
            backgroundColor: "var(--bg2)",
            padding: 20,
            height: "fit-content",
            position: "sticky",
            top: 24,
          }}
        >
          {activeTab === "rules" ? (
            /* RULES FORM */
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 20 }}>
                {formMode === "create" ? "➕ Yeni Rule Oluştur" : "✏️ Rule Düzenle"}
              </h2>

              {/* Type */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text)" }}>Rule Tipi *</label>
                <select
                  value={formData.type || ""}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as RuleType })}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: 14,
                    borderRadius: 8,
                    border: formErrors.type ? "1px solid #ef4444" : "1px solid rgba(156, 163, 175, 0.3)",
                    backgroundColor: "var(--bg2)",
                    color: "var(--text)",
                    cursor: "pointer",
                  }}
                >
                  <option value="new_user_register">new_user_register</option>
                  <option value="user_reported">user_reported</option>
                  <option value="suspicious_ip">suspicious_ip</option>
                  <option value="post_boosted">post_boosted</option>
                  <option value="server_high_cpu">server_high_cpu</option>
                  <option value="outbox_failed">outbox_failed</option>
                  <option value="login_failures">login_failures</option>
                </select>
                {formErrors.type && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{formErrors.type}</div>}
              </div>

              {/* Threshold */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text)" }}>Threshold *</label>
                <input
                  type="number"
                  value={formData.threshold || ""}
                  onChange={(e) => setFormData({ ...formData, threshold: parseInt(e.target.value) || 0 })}
                  placeholder="10"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: 14,
                    borderRadius: 8,
                    border: formErrors.threshold ? "1px solid #ef4444" : "1px solid rgba(156, 163, 175, 0.3)",
                    backgroundColor: "var(--bg2)",
                    color: "var(--text)",
                  }}
                />
                {formErrors.threshold && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{formErrors.threshold}</div>}
              </div>

              {/* Time Range */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text)" }}>Time Range (dk)</label>
                <input
                  type="number"
                  value={formData.timeRange || ""}
                  onChange={(e) => setFormData({ ...formData, timeRange: parseInt(e.target.value) || 60 })}
                  placeholder="60"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: 14,
                    borderRadius: 8,
                    border: "1px solid rgba(156, 163, 175, 0.3)",
                    backgroundColor: "var(--bg2)",
                    color: "var(--text)",
                  }}
                />
              </div>

              {/* Action */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text)" }}>Action</label>
                <select
                  value={formData.action || ""}
                  onChange={(e) => setFormData({ ...formData, action: e.target.value as ActionType })}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: 14,
                    borderRadius: 8,
                    border: "1px solid rgba(156, 163, 175, 0.3)",
                    backgroundColor: "var(--bg2)",
                    color: "var(--text)",
                    cursor: "pointer",
                  }}
                >
                  <option value="email">email</option>
                  <option value="sse">sse</option>
                  <option value="push">push</option>
                </select>
              </div>

              {/* Targets */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: "var(--text)" }}>Targets *</label>
                <input
                  type="text"
                  value={formData.targets?.join(", ") || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      targets: e.target.value
                        .split(",")
                        .map((t) => t.trim())
                        .filter((t) => t),
                    })
                  }
                  placeholder="all-admins veya admin@savnot.com, admin2@savnot.com"
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    fontSize: 14,
                    borderRadius: 8,
                    border: formErrors.targets ? "1px solid #ef4444" : "1px solid rgba(156, 163, 175, 0.3)",
                    backgroundColor: "var(--bg2)",
                    color: "var(--text)",
                  }}
                />
                {formErrors.targets && <div style={{ fontSize: 12, color: "#ef4444", marginTop: 4 }}>{formErrors.targets}</div>}
              </div>

              {/* Status Toggle */}
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}>
                  <input
                    type="checkbox"
                    checked={formData.status === "active"}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked ? "active" : "inactive" })}
                    style={{ cursor: "pointer" }}
                  />
                  <span style={{ fontSize: 14, fontWeight: 600, color: "var(--text)" }}>Aktif</span>
                </label>
              </div>

              {/* Buttons */}
              <div style={{ display: "flex", gap: 8 }}>
                {formMode === "create" ? (
                  <button
                    onClick={handleCreateRule}
                    disabled={formBusy}
                    style={{
                      flex: 1,
                      padding: "10px 16px",
                      fontSize: 14,
                      fontWeight: 700,
                      borderRadius: 8,
                      border: "1px solid rgba(34, 197, 94, 0.4)",
                      backgroundColor: "rgba(34, 197, 94, 0.1)",
                      color: "#22c55e",
                      cursor: formBusy ? "not-allowed" : "pointer",
                      opacity: formBusy ? 0.6 : 1,
                    }}
                  >
                    {formBusy ? "⏳ İşleniyor..." : "✅ Oluştur"}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={handleUpdateRule}
                      disabled={formBusy}
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        fontSize: 14,
                        fontWeight: 700,
                        borderRadius: 8,
                        border: "1px solid rgba(59, 130, 246, 0.4)",
                        backgroundColor: "rgba(59, 130, 246, 0.1)",
                        color: "#3b82f6",
                        cursor: formBusy ? "not-allowed" : "pointer",
                        opacity: formBusy ? 0.6 : 1,
                      }}
                    >
                      {formBusy ? "⏳ İşleniyor..." : "💾 Güncelle"}
                    </button>
                    <button
                      onClick={resetForm}
                      disabled={formBusy}
                      style={{
                        flex: 1,
                        padding: "10px 16px",
                        fontSize: 14,
                        fontWeight: 700,
                        borderRadius: 8,
                        border: "1px solid rgba(156, 163, 175, 0.4)",
                        backgroundColor: "rgba(156, 163, 175, 0.1)",
                        color: "#9ca3af",
                        cursor: formBusy ? "not-allowed" : "pointer",
                        opacity: formBusy ? 0.6 : 1,
                      }}
                    >
                      ✕ İptal
                    </button>
                  </>
                )}
              </div>
            </div>
          ) : !selectedAlert ? (
            /* ALERT EMPTY STATE */
            <div style={{ textAlign: "center", padding: "60px 20px", color: "var(--muted)" }}>
              <div style={{ fontSize: 64, marginBottom: 16 }}>🚨</div>
              <div style={{ fontSize: 16, fontWeight: 600 }}>Sol taraftan bir alert seç</div>
              <div style={{ fontSize: 14, marginTop: 8, opacity: 0.7 }}>Detaylı bilgiler burada görünecek</div>
            </div>
          ) : (
            /* ALERT DETAIL PANEL */
            <div>
              {/* Header */}
              <div style={{ marginBottom: 20, paddingBottom: 16, borderBottom: "1px solid rgba(156, 163, 175, 0.2)" }}>
                <h2 style={{ fontSize: 18, fontWeight: 800, margin: 0, marginBottom: 8 }}>🚨 Alert Detayı</h2>
                <div style={{ fontSize: 11, fontFamily: "monospace", color: "var(--muted)", wordBreak: "break-all" }}>
                  {selectedAlert._id}
                </div>
              </div>

              {/* Content */}
              <div style={{ display: "grid", gap: 10, marginBottom: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>Tip:</span>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedAlert.type}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>Severity:</span>
                  {getSeverityBadge(selectedAlert.severity)}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>Status:</span>
                  {getAlertStatusBadge(selectedAlert.status)}
                </div>
                {selectedAlert.source && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Kaynak:</span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{selectedAlert.source}</span>
                  </div>
                )}
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span style={{ fontSize: 13, color: "var(--muted)" }}>Zaman:</span>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 12 }}>{formatDate(selectedAlert.timestamp)}</div>
                    <div style={{ fontSize: 11, opacity: 0.7 }}>{timeAgo(selectedAlert.timestamp)}</div>
                  </div>
                </div>
                {selectedAlert.acknowledgedAt && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Acknowledged:</span>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12 }}>{formatDate(selectedAlert.acknowledgedAt)}</div>
                      {selectedAlert.acknowledgedBy && <div style={{ fontSize: 11, opacity: 0.7 }}>{selectedAlert.acknowledgedBy}</div>}
                    </div>
                  </div>
                )}
                {selectedAlert.resolvedAt && (
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, color: "var(--muted)" }}>Resolved:</span>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 12 }}>{formatDate(selectedAlert.resolvedAt)}</div>
                      {selectedAlert.resolvedBy && <div style={{ fontSize: 11, opacity: 0.7 }}>{selectedAlert.resolvedBy}</div>}
                    </div>
                  </div>
                )}
              </div>

              {/* Message */}
              <div style={{ marginBottom: 20 }}>
                <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>📝 Mesaj</h3>
                <div
                  style={{
                    padding: 12,
                    borderRadius: 8,
                    backgroundColor: "rgba(156, 163, 175, 0.08)",
                    fontSize: 13,
                    wordBreak: "break-word",
                  }}
                >
                  {selectedAlert.message}
                </div>
              </div>

              {/* Stack Trace */}
              {selectedAlert.stackTrace && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>🔧 Stack Trace</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      fontSize: 11,
                      fontFamily: "monospace",
                      wordBreak: "break-word",
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{selectedAlert.stackTrace}</pre>
                  </div>
                </div>
              )}

              {/* Meta */}
              {selectedAlert.meta && (
                <div style={{ marginBottom: 20 }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}>🔖 Metadata</h3>
                  <div
                    style={{
                      padding: 12,
                      borderRadius: 8,
                      backgroundColor: "rgba(156, 163, 175, 0.08)",
                      fontSize: 11,
                      fontFamily: "monospace",
                      wordBreak: "break-word",
                      maxHeight: 200,
                      overflowY: "auto",
                    }}
                  >
                    <pre style={{ margin: 0, whiteSpace: "pre-wrap" }}>{JSON.stringify(selectedAlert.meta, null, 2)}</pre>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {selectedAlert.status === "open" && (
                  <button
                    onClick={() => handleAcknowledgeAlert(selectedAlert._id)}
                    disabled={actionBusy}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      fontSize: 14,
                      fontWeight: 700,
                      borderRadius: 8,
                      border: "1px solid rgba(251, 191, 36, 0.4)",
                      backgroundColor: "rgba(251, 191, 36, 0.1)",
                      color: "#fbbf24",
                      cursor: actionBusy ? "not-allowed" : "pointer",
                      opacity: actionBusy ? 0.6 : 1,
                    }}
                  >
                    {actionBusy ? "⏳ İşleniyor..." : "⚠ Acknowledge"}
                  </button>
                )}
                {selectedAlert.status !== "resolved" && (
                  <button
                    onClick={() => handleResolveAlert(selectedAlert._id)}
                    disabled={actionBusy}
                    style={{
                      width: "100%",
                      padding: "10px 16px",
                      fontSize: 14,
                      fontWeight: 700,
                      borderRadius: 8,
                      border: "1px solid rgba(34, 197, 94, 0.4)",
                      backgroundColor: "rgba(34, 197, 94, 0.1)",
                      color: "#22c55e",
                      cursor: actionBusy ? "not-allowed" : "pointer",
                      opacity: actionBusy ? 0.6 : 1,
                    }}
                  >
                    {actionBusy ? "⏳ İşleniyor..." : "✔ Resolve"}
                  </button>
                )}
                <button
                  onClick={() => setSelectedAlert(null)}
                  style={{
                    width: "100%",
                    padding: "10px 16px",
                    fontSize: 14,
                    fontWeight: 700,
                    borderRadius: 8,
                    border: "1px solid rgba(156, 163, 175, 0.4)",
                    backgroundColor: "rgba(156, 163, 175, 0.1)",
                    color: "#9ca3af",
                    cursor: "pointer",
                  }}
                >
                  Kapat
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
